import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { DataProvider } from './contexts/DataContext';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import DashboardLayout from './components/Layout/DashboardLayout';
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';

const Dashboard = lazy(() => import('./pages/Dashboard'));
const Analytics = lazy(() => import('./pages/Analytics'));
const FleetManagement = lazy(() => import('./pages/FleetManagement'));
const Bookings = lazy(() => import('./pages/Bookings'));
const BookingTracker = lazy(() => import('./pages/BookingTracker'));
const BlogManagement = lazy(() => import('./pages/BlogManagement'));
const BlogEditor = lazy(() => import('./components/Blog/BlogEditor'));
const WebsiteCMS = lazy(() => import('./pages/WebsiteCMS'));
const UserManagement = lazy(() => import('./pages/UserManagement'));
const CustomerManagement = lazy(() => import('./pages/CustomerManagement'));
const Discounts = lazy(() => import('./pages/Discounts'));
const SeasonalPricing = lazy(() => import('./pages/SeasonalPricing'));
const PriceManagement = lazy(() => import('./pages/PriceManagement'));
const MaintenanceScheduler = lazy(() => import('./pages/MaintenanceScheduler'));
const IssueTracker = lazy(() => import('./pages/IssueTracker'));
const VehicleCompliance = lazy(() => import('./pages/VehicleCompliance'));
const Settings = lazy(() => import('./pages/Settings'));
const SystemHealth = lazy(() => import('./pages/SystemHealth'));
const WebsiteAnalytics = lazy(() => import('./pages/WebsiteAnalytics'));
const Notifications = lazy(() => import('./pages/Notifications'));
const SEODashboard = lazy(() => import('./pages/SEODashboard'));
const AuditLogs = lazy(() => import('./pages/AuditLogs'));
const RecycleBin = lazy(() => import('./pages/RecycleBin'));

const RouteLoader = () => (
  <div className="min-h-[50vh] flex items-center justify-center">
    <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
  </div>
);

const RedirectIfAuthenticated: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

function App() {
  return (
    <AuthProvider>
      <NotificationProvider>
        <DataProvider>
        <Router>
          <Suspense fallback={<RouteLoader />}>
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/login" element={
              <RedirectIfAuthenticated>
                <LoginPage />
              </RedirectIfAuthenticated>
            } />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/" element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }>
              <Route path="dashboard" element={
                <ProtectedRoute requiredPermission="view_dashboard">
                  <Dashboard />
                </ProtectedRoute>
              } />
              <Route path="analytics" element={
                <ProtectedRoute requiredPermission="view_analytics">
                  <Analytics />
                </ProtectedRoute>
              } />
              <Route path="fleet" element={
                <ProtectedRoute requiredPermission="manage_fleet">
                  <FleetManagement />
                </ProtectedRoute>
              } />
              <Route path="maintenance" element={
                <ProtectedRoute requiredPermission="manage_fleet">
                  <MaintenanceScheduler />
                </ProtectedRoute>
              } />
              <Route path="issues" element={
                <ProtectedRoute requiredPermission="manage_fleet">
                  <IssueTracker />
                </ProtectedRoute>
              } />
              <Route path="compliance" element={
                <ProtectedRoute requiredPermission="manage_fleet">
                  <VehicleCompliance />
                </ProtectedRoute>
              } />
              <Route path="bookings" element={
                <ProtectedRoute requiredPermission="manage_bookings">
                  <Bookings />
                </ProtectedRoute>
              } />
              <Route path="booking-tracker" element={
                <ProtectedRoute requiredPermission="manage_bookings">
                  <BookingTracker />
                </ProtectedRoute>
              } />
              <Route path="blog" element={
                <ProtectedRoute requiredPermission="view_dashboard">
                  <BlogManagement />
                </ProtectedRoute>
              } />
              <Route path="blog/editor" element={
                <ProtectedRoute requiredPermission="view_dashboard">
                  <BlogEditor />
                </ProtectedRoute>
              } />
              <Route path="blog/editor/:id" element={
                <ProtectedRoute requiredPermission="view_dashboard">
                  <BlogEditor />
                </ProtectedRoute>
              } />
              <Route path="pages" element={
                <ProtectedRoute requiredPermission="view_dashboard">
                  <WebsiteCMS />
                </ProtectedRoute>
              } />
              <Route path="notifications" element={
                <ProtectedRoute requiredPermission="view_dashboard">
                  <Notifications />
                </ProtectedRoute>
              } />
              <Route path="users" element={
                <ProtectedRoute requiredPermission="manage_users">
                  <UserManagement />
                </ProtectedRoute>
              } />
              <Route path="discounts" element={
                <ProtectedRoute requiredPermission="manage_discounts">
                  <Discounts />
                </ProtectedRoute>
              } />
              <Route path="seasonal-pricing" element={
                <ProtectedRoute requiredPermission="manage_discounts">
                  <SeasonalPricing />
                </ProtectedRoute>
              } />
              <Route path="price-management" element={
                <ProtectedRoute requiredPermission="manage_fleet">
                  <PriceManagement />
                </ProtectedRoute>
              } />
              <Route path="customers" element={
                <ProtectedRoute requiredPermission="view_dashboard">
                  <CustomerManagement />
                </ProtectedRoute>
              } />
              <Route path="settings" element={
                <ProtectedRoute requiredPermission="manage_settings">
                  <Settings />
                </ProtectedRoute>
              } />
              <Route path="system-health" element={
                <ProtectedRoute requiredPermission="view_dashboard">
                  <SystemHealth />
                </ProtectedRoute>
              } />
              <Route path="website-analytics" element={
                <ProtectedRoute requiredPermission="view_analytics">
                  <WebsiteAnalytics />
                </ProtectedRoute>
              } />
              <Route path="seo" element={
                <ProtectedRoute requiredPermission="view_dashboard">
                  <SEODashboard />
                </ProtectedRoute>
              } />
              <Route path="recycle-bin" element={
                <ProtectedRoute requiredPermission="manage_settings">
                  <RecycleBin />
                </ProtectedRoute>
              } />
              <Route path="audit-logs" element={
                <ProtectedRoute requiredRole="super_admin">
                  <AuditLogs />
                </ProtectedRoute>
              } />
            </Route>
          </Routes>
          </Suspense>
        </Router>
        </DataProvider>
        <Toaster 
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#363636',
              color: '#fff',
            },
            success: {
              duration: 3000,
              iconTheme: {
                primary: '#4ade80',
                secondary: '#fff',
              },
            },
            error: {
              duration: 4000,
              iconTheme: {
                primary: '#ef4444',
                secondary: '#fff',
              },
            },
          }}
        />
      </NotificationProvider>
    </AuthProvider>
  );
}

export default App;
